import { DateTime } from '@ksyos/date-time';
import { Box, Col, DropdownFilter, Heading, Row, Text, TextInput } from '@ksyos/design-system';
import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { EpisodeCategory } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { FormRow, StackedFormGroup } from '@frontend/components/elements/forms';
import { translatePatientTaskType } from '@frontend/translate/custom';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type {
    IPatientTasksUsersAndProfessionsFilter,
    IPatientTasksUsersAndProfessionsFilterData,
} from '../../../../../common/validation/patientTasksUsersAndProfessionsFilter';
import type { IReduxState } from '../../../../configureStore';
import { WorkListFilter } from '../../../../../common/enums/workListFilter';
import { fetchJson } from '../../../../store/fetch';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { DateInput, HeaderButton, InputCheckbox } from '../../../elements/common';
import { InputControl } from '../../../elements/forms/inputControl/InputControl';
import { StyledBox } from '../../../elements/styledBox';
import { getTaskGroupsAndTypes } from '../../outpatientClinic/utils/outpatientClinicTaskTypesUtils';
import { RequestListPractitionerFilter } from './RequestListPractitionerFilter';
import { RequestListServiceFilter } from './RequestListServiceFilter';

export interface ISearchFilter {
    endDate: string | null;
    practitionerId: string | null;
    serviceId: string | null;
    startDate: string | null;
    text: string;
    typeOfTasksSelected: string[];
    usersAndProfessions: IPatientTasksUsersAndProfessionsFilter;
}

export interface IRequestsListHeaderProps {
    applyFilter: (searchFilter: ISearchFilter) => Promise<void>;
    fetchedPatientTasksEpisodeCategories: EpisodeCategory[];
    fetchedPatientTasksTypes: string[];
    filterWorkList: (filter: WorkListFilter) => Promise<void>;
    onSearchFilterChange: (searchFilter: ISearchFilter) => Promise<void>;
    searchFilter: ISearchFilter;
    showOngoingRequests?: boolean;
    showPatientTasks: boolean;
    workListFilter?: WorkListFilter;
}

interface IDateInputErrors {
    endDate: string;
    startDate: string;
}

export function RequestsListHeader(props: IRequestsListHeaderProps): JSX.Element {
    const abortSignal = useAbortSignal();
    const abortableFetch = useAbortableFetch();
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const [searchText, setSearchText] = useState<string>('');
    const [hasColleagues, setHasColleagues] = useState<boolean>(false);
    const [usersAndProfessionsFilterData, setUsersAndProfessionsFilterData] =
        useState<IPatientTasksUsersAndProfessionsFilterData>({} as IPatientTasksUsersAndProfessionsFilterData);
    const [searchTypeOfTasksSelected, setSearchTypeOfTasksSelected] = useState<string[]>([]);
    const [countUsersAndProfessionsFilterData, setCountUsersAndProfessionsFilterData] = useState<number>(0);
    const [dateErrors, setDateErrors] = useState<IDateInputErrors>({ endDate: '', startDate: '' });
    const [taskGroupsAndTypes, setTaskGroupsAndTypes] = useState<Awaited<ReturnType<typeof getTaskGroupsAndTypes>>>([]);

    const translate = getLanguage();

    useEffect(() => {
        setSearchText(props.searchFilter.text);
    }, [props.searchFilter.text]);

    useEffect(() => {
        setSearchTypeOfTasksSelected(props.searchFilter.typeOfTasksSelected);
    }, [props.searchFilter.typeOfTasksSelected]);

    useEffect(() => {
        countUsersAndProfessionsSelected();
    }, [props.searchFilter]);

    useEffect(() => {
        loadPractitioners();
        loadFilter();
        loadTaskGroupsAndTypes();
    }, []);

    async function loadTaskGroupsAndTypes() {
        const results = await getTaskGroupsAndTypes({
            abortSignal,
            episodeCategory: props.fetchedPatientTasksEpisodeCategories.map((name) => ({
                name,
            })),
        });

        setTaskGroupsAndTypes(results);
    }

    return (
        <>
            <Box
                mb={8}
                mt={10}
            >
                <Row
                    columnGap={[2, 4]}
                    rowGap={[2, 4]}
                >
                    <Col span={[12, null, null, 6]}>
                        <Heading
                            mb={[4, null, null, 0]}
                            size="xlarge"
                        >
                            {translate.request.startWith}
                        </Heading>
                    </Col>
                    {props.workListFilter !== WorkListFilter.patientTasks && (
                        <Col span={[12, null, null, 6]}>
                            <TextInput
                                maxLength={255}
                                onChange={(event) => handleChangeSearchText(event.target.value)}
                                onEnterKeyPress={() => handleChangeSearchText(searchText)}
                                onReset={() => handleChangeSearchText('')}
                                placeholder={translate.request.searchInWorklist}
                                size="medium"
                                suffix={{
                                    icon: 'search',
                                    labelText: 'aria labelled text',
                                    onSuffixClick: () => handleChangeSearchText(searchText),
                                }}
                                type="text"
                                value={searchText}
                                variant="flushed"
                            />
                        </Col>
                    )}
                </Row>
            </Box>
            <Box mb={8}>
                <Row
                    columnGap={[2, 4]}
                    rowGap={[2, 4]}
                >
                    <Col span={[12, null, null, 6]}>
                        <Box
                            alignItems="center"
                            display="flex"
                            flexWrap="wrap"
                            sx={{ button: { mb: 3, mr: 3 } }}
                        >
                            <HeaderButton
                                automation="btnMyRequests"
                                onClick={async () => await onClickHeaderButton(WorkListFilter.myRequests)}
                                selected={props.workListFilter === WorkListFilter.myRequests}
                            >
                                {translate.request.myRequests}
                            </HeaderButton>
                            {props.showPatientTasks && (
                                <HeaderButton
                                    automation="btnPatientTasks"
                                    onClick={async () => await onClickHeaderButton(WorkListFilter.patientTasks)}
                                    selected={props.workListFilter === WorkListFilter.patientTasks}
                                >
                                    {translate.request.patientTasks}
                                </HeaderButton>
                            )}
                            <HeaderButton
                                automation="btnColleagueRequests"
                                onClick={async () => await onClickHeaderButton(WorkListFilter.colleagueRequests)}
                                selected={props.workListFilter === WorkListFilter.colleagueRequests}
                            >
                                {translate.request.colleagueRequests}
                            </HeaderButton>
                            {props.showOngoingRequests && (
                                <HeaderButton
                                    automation="btnOngoingRequests"
                                    onClick={async () => await onClickHeaderButton(WorkListFilter.ongoingRequests)}
                                    selected={props.workListFilter === WorkListFilter.ongoingRequests}
                                >
                                    {translate.request.ongoingRequests}
                                </HeaderButton>
                            )}
                            <HeaderButton
                                automation="btnClosedRequests"
                                onClick={async () => await onClickHeaderButton(WorkListFilter.closedRequests)}
                                selected={props.workListFilter === WorkListFilter.closedRequests}
                            >
                                {translate.request.closedRequests}
                            </HeaderButton>
                        </Box>
                    </Col>
                    {props.workListFilter !== WorkListFilter.patientTasks && (
                        <>
                            <Col span={[12, null, null, 3]}>
                                {props.workListFilter !== WorkListFilter.myRequests && hasColleagues && (
                                    <RequestListPractitionerFilter
                                        onSearchFilterChange={props.onSearchFilterChange}
                                        searchFilter={props.searchFilter}
                                    />
                                )}
                            </Col>
                            <Col span={[12, null, null, 3]}>
                                <RequestListServiceFilter
                                    onSearchFilterChange={props.onSearchFilterChange}
                                    searchFilter={props.searchFilter}
                                    workListFilter={props.workListFilter || WorkListFilter.closedRequests}
                                />
                            </Col>
                        </>
                    )}
                </Row>
            </Box>

            {props.workListFilter === WorkListFilter.patientTasks && (
                <StyledBox>
                    <TextInput
                        maxLength={255}
                        onChange={(event) => handleChangeSearchText(event.target.value)}
                        onEnterKeyPress={() => handleChangeSearchText(searchText)}
                        onReset={() => handleChangeSearchText('')}
                        placeholder={translate.request.searchInPatientTasks}
                        size="medium"
                        suffix={{
                            icon: 'search',
                            labelText: 'aria labelled text',
                            onSuffixClick: () => handleChangeSearchText(searchText),
                        }}
                        type="text"
                        value={props.searchFilter.text}
                        variant="outline"
                    />

                    <FormRow>
                        <StackedFormGroup>
                            <DateInput
                                error={dateErrors.startDate}
                                maxDate={props.searchFilter.endDate}
                                onChange={async (date) => validateAndHandleDateInputChange('startDate', date)}
                                placeholder={translate.common.dateFormat}
                                value={props.searchFilter.startDate}
                            />
                        </StackedFormGroup>
                    </FormRow>

                    <Box>
                        <Text
                            size="sm"
                            sx={{ mt: 2.5 }}
                            whiteSpace="nowrap"
                        >
                            t/m
                        </Text>
                    </Box>

                    <FormRow>
                        <StackedFormGroup>
                            <DateInput
                                error={dateErrors.endDate}
                                minDate={props.searchFilter.startDate}
                                onChange={async (date) => validateAndHandleDateInputChange('endDate', date)}
                                placeholder={translate.common.dateFormat}
                                value={props.searchFilter.endDate}
                            />
                        </StackedFormGroup>
                    </FormRow>

                    {renderTypeOfPatientTaskFilter()}
                    {renderUsersAndProfessionsFilter()}
                </StyledBox>
            )}
        </>
    );

    async function validateAndHandleDateInputChange(dateKey: 'startDate' | 'endDate', date: string | null) {
        const [startDate, endDate] =
            dateKey === 'startDate' ? [date, props.searchFilter.endDate] : [props.searchFilter.startDate, date];

        let error = '';

        if (date) {
            if (!DateTime.from(date).isValid()) {
                error = 'koi_date';
            } else if (startDate && endDate && DateTime.from(startDate).isAfter(endDate)) {
                error = 'koi_endDate';
            }
        }

        setDateErrors({ ...dateErrors, [dateKey]: error });

        if (!error) {
            await props.onSearchFilterChange({ ...props.searchFilter, [dateKey]: date });
        }
    }

    async function loadPractitioners(): Promise<void> {
        const result = await abortableFetch.fetchJson('/api/practitioners/list', 'POST', {
            filters: {
                organizationId,
            },
        });
        if (result.json.items.length > 1) {
            setHasColleagues(true);
        }
    }

    async function onClickHeaderButton(workListFilter: WorkListFilter): Promise<void> {
        await props.onSearchFilterChange({
            endDate: null,
            practitionerId: null,
            serviceId: null,
            startDate: null,
            text: '',
            typeOfTasksSelected: [],
            usersAndProfessions: {},
        });
        if (workListFilter === WorkListFilter.patientTasks) {
            clearDropdownFilters();
        }
        props.filterWorkList(workListFilter);
    }

    async function loadFilter() {
        const result = await fetchJson('/api/patientTasks/getUsersAndProfessionsFilterData', 'GET');
        setUsersAndProfessionsFilterData(result.json.filterData);
    }

    function renderUsersAndProfessionsFilter() {
        if (!Object.keys(usersAndProfessionsFilterData).length) {
            return;
        }

        return (
            <DropdownFilter
                count={countUsersAndProfessionsFilterData}
                label={translate.procedure.performer}
                onReset={() => props.onSearchFilterChange({ ...props.searchFilter, usersAndProfessions: {} })}
                onSubmit={() =>
                    props.onSearchFilterChange({
                        ...props.searchFilter,
                        usersAndProfessions: props.searchFilter.usersAndProfessions,
                    })
                }
            >
                {Object.entries(usersAndProfessionsFilterData).map(([profession, practitioners]) => {
                    return (
                        <Fragment key={`UsersAndProfessionsFilter_${profession}`}>
                            <InputCheckbox
                                checked={!!props.searchFilter.usersAndProfessions[profession]}
                                key={`${profession}_group`}
                                onChange={(value) => {
                                    let newUsersAndProfessions = props.searchFilter.usersAndProfessions;
                                    if (value) {
                                        newUsersAndProfessions[profession] = [] as string[];
                                    } else {
                                        newUsersAndProfessions = Object.fromEntries(
                                            Object.entries(props.searchFilter.usersAndProfessions).filter(
                                                ([p]) => p !== profession
                                            )
                                        );
                                    }
                                    props.applyFilter({
                                        ...props.searchFilter,
                                        usersAndProfessions: newUsersAndProfessions,
                                    });
                                }}
                                text={translate.professionEnum[profession as keyof typeof translate.professionEnum]}
                            />
                            <InputControl indented={true}>
                                {practitioners.map((practitioner, i) => (
                                    <InputCheckbox
                                        checked={
                                            !!props.searchFilter.usersAndProfessions[profession]?.includes(
                                                practitioner.practitionerId
                                            )
                                        }
                                        key={`${profession}_${i}`}
                                        onChange={(value) => {
                                            const newUsersAndProfessions = props.searchFilter.usersAndProfessions;
                                            if (value) {
                                                if (!newUsersAndProfessions[profession]) {
                                                    newUsersAndProfessions[profession] = [] as string[];
                                                }
                                                newUsersAndProfessions[profession].push(practitioner.practitionerId);
                                            } else {
                                                newUsersAndProfessions[profession].splice(
                                                    newUsersAndProfessions[profession].indexOf(
                                                        practitioner.practitionerId
                                                    ),
                                                    1
                                                );
                                            }
                                            props.applyFilter({
                                                ...props.searchFilter,
                                                usersAndProfessions: newUsersAndProfessions,
                                            });
                                        }}
                                        text={practitioner.practitionerReadableName}
                                    />
                                ))}
                            </InputControl>
                        </Fragment>
                    );
                })}
            </DropdownFilter>
        );
    }

    function countUsersAndProfessionsSelected() {
        const count = Object.entries(props.searchFilter.usersAndProfessions).reduce(
            (acc, [, value]) => acc + 1 + value.length,
            0
        );
        setCountUsersAndProfessionsFilterData(count);
    }

    function clearDropdownFilters() {
        setSearchTypeOfTasksSelected([]);
    }

    function renderTypeOfPatientTaskFilter(): JSX.Element | null {
        if (props.workListFilter !== WorkListFilter.patientTasks || props.fetchedPatientTasksTypes.length === 0) {
            return null;
        }

        const dropdownSections: JSX.Element[] = [];

        for (const episodeCategory of props.fetchedPatientTasksEpisodeCategories) {
            const taskTypeEpisodeCategory = taskGroupsAndTypes.find((entry) => entry.name === episodeCategory);

            if (!taskTypeEpisodeCategory) {
                continue;
            }

            for (const section of taskTypeEpisodeCategory.groups) {
                const types = [];

                for (const type of section.types) {
                    if (props.fetchedPatientTasksTypes.includes(type.name)) {
                        types.push(type.name);
                    }
                }

                if (types.length > 0) {
                    dropdownSections.push(renderTypeOfPatientTaskDropdownSection(episodeCategory, section.name, types));
                }
            }
        }

        return (
            <DropdownFilter
                count={searchTypeOfTasksSelected.length}
                label={translate.request.taskType}
                noPadding
                onReset={async () => {
                    clearDropdownFilters();
                    await props.onSearchFilterChange({
                        ...props.searchFilter,
                        typeOfTasksSelected: [],
                    });
                }}
                onSubmit={async () => {
                    await props.onSearchFilterChange({
                        ...props.searchFilter,
                        typeOfTasksSelected: searchTypeOfTasksSelected,
                    });
                }}
            >
                <DropdownFilter.Accordion type="multiple">{dropdownSections}</DropdownFilter.Accordion>
            </DropdownFilter>
        );
    }

    function renderTypeOfPatientTaskDropdownSection(
        episodeCategoryName: string,
        section: string,
        patientTaskTypes: string[]
    ) {
        return (
            <DropdownFilter.AccordionItem
                key={section}
                label={translatePatientTaskType(episodeCategoryName, section)}
                value={section}
            >
                {patientTaskTypes.map((taskType) => (
                    <InputCheckbox
                        checked={!!searchTypeOfTasksSelected.find((item) => item === taskType)}
                        key={`${section}|${taskType}`}
                        onChange={async (value) => {
                            const updatedTypeOfTasksSelected = [...searchTypeOfTasksSelected];
                            if (value) {
                                updatedTypeOfTasksSelected.push(taskType);
                            } else {
                                const index = searchTypeOfTasksSelected.indexOf(taskType as string);
                                updatedTypeOfTasksSelected.splice(index, 1);
                            }
                            setSearchTypeOfTasksSelected(updatedTypeOfTasksSelected);
                        }}
                        text={translatePatientTaskType(episodeCategoryName, taskType)}
                    />
                ))}
            </DropdownFilter.AccordionItem>
        );
    }

    async function handleChangeSearchText(text: string) {
        props.onSearchFilterChange({ ...props.searchFilter, text });
    }
}
